import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom"; // Import useNavigate for page redirection
import Navbar from "../components/navbar";
import Footer from "../components/footer";

export default function DataInput() {
    const { t } = useTranslation();
    const navigate = useNavigate();  // Initialize the navigate function

    const [jsonData, setJsonData] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [responseMessage, setResponseMessage] = useState('');

    // Check if the user is an admin before loading the page
    useEffect(() => {
        const userRole = localStorage.getItem("role");
        if (userRole !== "admin") {
            navigate("/"); // Redirect to the home page if not an admin
        }
    }, [navigate]);

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.type !== 'application/json') {
            setError(t("datainput.invalid_file"));
            setJsonData(null);
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const parsed = JSON.parse(event.target.result);
                setJsonData(parsed);
                setError('');
                setResponseMessage('');
            } catch (err) {
                setError(t("datainput.invalid_json"));
                setJsonData(null);
                setResponseMessage('');
            }
        };
        reader.readAsText(file);
    };

    // Extract measurements if available
    const measurements = jsonData?.measurements || [];

    // Summary data calculations
    const locationCount = jsonData?.locations?.length || 0;
    const measurementCount = measurements.length;

    // Calculate date range
    let minDate = null;
    let maxDate = null;
    if (measurementCount > 0) {
        const dates = measurements
            .map(m => new Date(m.date))
            .filter(d => !isNaN(d));
        if (dates.length > 0) {
            minDate = new Date(Math.min(...dates));
            maxDate = new Date(Math.max(...dates));
        }
    }

    // Collect unique toxin names
    const uniqueToxinsSet = new Set();
    measurements.forEach(m => {
        if (m.toxins) {
            Object.keys(m.toxins).forEach(toxin => uniqueToxinsSet.add(toxin));
        }
    });
    const uniqueToxins = Array.from(uniqueToxinsSet);

    const handleLoadData = async () => {
        if (!jsonData) {
            setError(t("datainput.no_data_error"));
            return;
        }

        const confirmed = window.confirm(t("datainput.confirm_load"));
        if (!confirmed) return;

        setLoading(true);
        setError('');
        setResponseMessage('');

        try {
            const response = await fetch("https://shellfish-pollution-prediction-production.up.railway.app/api/load-json", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    // Add your API key here if your backend requires it:
                    // "X-API-Key": "your_api_key_here",
                },
                body: JSON.stringify(jsonData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                let errorMessage = t("datainput.load_failed");

                if (errorData.detail) {
                    if (typeof errorData.detail === "string") {
                        errorMessage = errorData.detail;
                    } else if (Array.isArray(errorData.detail)) {
                        errorMessage = errorData.detail.map(item =>
                            typeof item === "string" ? item : JSON.stringify(item)
                        ).join(", ");
                    } else if (typeof errorData.detail === "object") {
                        errorMessage = JSON.stringify(errorData.detail);
                    }
                }

                throw new Error(errorMessage);
            }

            const data = await response.json();
            setResponseMessage(data.message || t("datainput.load_success"));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-gray-50">
            <Navbar />

            <main className="flex-grow container mx-auto px-4 py-12">
                <div className="w-full max-w-5xl mx-auto bg-white p-8 rounded-xl shadow-md">
                    <h2 className="text-3xl font-bold mb-6 text-center text-gray-800">
                        {t("datainput.upload_title")}
                    </h2>

                    <label className="block mb-4">
                        <span className="text-gray-700 font-medium">
                            {t("datainput.choose_file")}
                        </span>
                        <input
                            type="file"
                            accept=".json"
                            onChange={handleFileUpload}
                            className="mt-2 w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                    </label>

                    {error && (
                        <p className="text-red-600 font-medium mb-4">{error}</p>
                    )}

                    {jsonData && (
                        <>
                            {/* Data Summary */}
                            <div className="mb-6 bg-gray-100 p-6 rounded-lg">
                                <h3 className="text-lg font-semibold mb-4 text-gray-700">
                                    {t("datainput.data_summary")}
                                </h3>
                                <p><strong>{t("datainput.total_locations")}:</strong> {locationCount}</p>
                                <p><strong>{t("datainput.total_measurements")}:</strong> {measurementCount}</p>
                                <p>
                                    <strong>{t("datainput.date_range")}:</strong>{" "}
                                    {minDate && maxDate ? (
                                        <>
                                            {minDate.toLocaleDateString()} - {maxDate.toLocaleDateString()}
                                        </>
                                    ) : (
                                        t("datainput.none")
                                    )}
                                </p>
                                <p>
                                    <strong>{t("datainput.unique_toxins")}:</strong> {uniqueToxins.length > 0 ? uniqueToxins.join(", ") : t("datainput.none")}
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Raw JSON */}
                                <div className="bg-gray-100 p-4 rounded-lg overflow-x-auto max-h-[400px]">
                                    <h3 className="text-lg font-semibold mb-2 text-gray-700">
                                        {t("datainput.raw_json")}
                                    </h3>
                                    <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                                        {JSON.stringify(jsonData, null, 2)}
                                    </pre>
                                </div>

                                {/* Table View */}
                                <div className="bg-gray-100 p-4 rounded-lg overflow-x-auto max-h-[400px]">
                                    <h3 className="text-lg font-semibold mb-2 text-gray-700">
                                        {t("datainput.table_view")}
                                    </h3>
                                    {measurements.length > 0 ? (
                                        <table className="w-full text-sm text-left border">
                                            <thead>
                                                <tr className="bg-gray-200">
                                                    <th className="p-2 border">{t("datainput.location")}</th>
                                                    <th className="p-2 border">{t("datainput.toxin")}</th>
                                                    <th className="p-2 border">{t("datainput.toxin_value")}</th>
                                                    <th className="p-2 border">{t("datainput.species")}</th>
                                                    <th className="p-2 border">{t("datainput.date")}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {measurements.map((m, idx) =>
                                                    Object.entries(m.toxins).map(([toxinName, toxinValue], toxinIdx) => (
                                                        <tr key={`${idx}-${toxinIdx}`} className="odd:bg-white even:bg-gray-50">
                                                            <td className="p-2 border">{m.location}</td>
                                                            <td className="p-2 border">{toxinName}</td>
                                                            <td className="p-2 border">{toxinValue}</td>
                                                            <td className="p-2 border">{m.species}</td>
                                                            <td className="p-2 border">{m.date}</td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <p className="text-gray-600">{t("datainput.no_measurements")}</p>
                                    )}
                                </div>
                            </div>

                            {/* Load Data Button */}
                            <div className="mt-6 flex justify-center">
                                <button
                                    onClick={handleLoadData}
                                    disabled={loading}
                                    className={`px-6 py-3 rounded font-semibold text-white ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
                                >
                                    {loading ? t("datainput.loading") : t("datainput.load_to_database")}
                                </button>
                            </div>

                            {/* Response message */}
                            {responseMessage && (
                                <p className="mt-4 text-green-600 font-medium text-center">{responseMessage}</p>
                            )}
                        </>
                    )}
                </div>
            </main>

            <Footer />
        </div>
    );
}
